import { useCallback, useEffect, useMemo, useState } from 'react';
import { useApp } from '../../../../context/AppContext';
import {
  loadConnectAndInitialize,
  StripeConnectInstance,
} from '@stripe/connect-js';
import {
  createPaymentAccount,
  getAccountSession,
  getOnboardingStatus,
  getPublishableKey,
} from '../../../../services/PaymentService';
import { Loader } from '@mantine/core';
import {
  ConnectAccountOnboarding,
  ConnectComponentsProvider,
} from '@stripe/react-connect-js';

export const StripeIFrameWrapper = ({ onClose }: { onClose: () => void }) => {
  const { profile, token, onSetProfile } = useApp();
  const [stripeConnectInstance, setStripeConnectInstance] = useState<
    StripeConnectInstance | undefined
  >(undefined);
  const [isLoading, setIsLoading] = useState(true);

  const paymentStatus = useMemo(() => {
    if (
      profile &&
      'paymentAccountDetailsSubmitted' in profile &&
      profile.paymentAccountDetailsSubmitted &&
      'paymentAccountChargesEnabled' in profile &&
      profile.paymentAccountChargesEnabled &&
      'paymentAccountPayoutsEnabled' in profile &&
      profile.paymentAccountPayoutsEnabled
    ) {
      return 'Verified';
    } else if (
      profile &&
      'detailsSubmitted' in profile &&
      profile.detailsSubmitted
    ) {
      return 'Processing';
    } else {
      return 'None';
    }
  }, [profile]);

  const close = useCallback(async () => {
    const onboardingStatus = await getOnboardingStatus(token);
    if (profile) {
      profile.paymentAccountDetailsSubmitted =
        onboardingStatus.detailsSubmitted;
      profile.paymentAccountChargesEnabled = onboardingStatus.chargesEnabled;
      profile.paymentAccountPayoutsEnabled = onboardingStatus.payoutsEnabled;
      onSetProfile({ ...profile });
    }
    onClose();
  }, [token, profile, onSetProfile]);

  useEffect(() => {
    let isMounted = true;
    const initializeStripe = async () => {
      setIsLoading(true);
      try {
        if (paymentStatus === 'None' && profile) {
          if (!profile.paymentAccountId) {
            profile.paymentAccountId = (
              await createPaymentAccount(token)
            ).accountId;
          }
          const publishableKey = await getPublishableKey(token);
          const instance = await loadConnectAndInitialize({
            publishableKey,
            fetchClientSecret: async () => {
              const { clientSecret } = await getAccountSession(token);
              return clientSecret;
            },
          });
          if (isMounted) {
            setStripeConnectInstance(instance);
          }
        } else if (paymentStatus === 'Processing') {
          const onboardingStatus = await getOnboardingStatus(token);
          if (profile) {
            profile.paymentAccountDetailsSubmitted =
              onboardingStatus.detailsSubmitted;
            profile.paymentAccountChargesEnabled =
              onboardingStatus.chargesEnabled;
            profile.paymentAccountPayoutsEnabled =
              onboardingStatus.payoutsEnabled;
            onSetProfile({ ...profile });
          }
        }
      } catch (error) {
        console.error('Error handling Stripe button click:', error);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    initializeStripe();
    return () => {
      isMounted = false;
    };
  }, [paymentStatus, profile, token, onSetProfile]);

  if (isLoading) {
    return <Loader />;
  }

  if (!stripeConnectInstance) {
    return null;
  }

  return (
    <>
      <ConnectComponentsProvider connectInstance={stripeConnectInstance}>
        <ConnectAccountOnboarding onExit={close} />
      </ConnectComponentsProvider>
    </>
  );
};
